import { Controller } from "@hotwired/stimulus"
import { Modal } from "bootstrap"

export default class extends Controller {
  static values = {
    token: String,
    currentStatus: String,
    entityToken: String
  }

  connect() {
    // Add CSS for status cards
    this.injectStyles()
  }

  open(event) {
    event.preventDefault()

    // Find the modal in the DOM
    const modalElement = document.querySelector('.decision-modal')
    if (!modalElement) {
      console.error('Decision modal not found in DOM')
      return
    }

    // Get or create Bootstrap modal instance
    let modalInstance = Modal.getInstance(modalElement)
    if (!modalInstance) {
      modalInstance = new Modal(modalElement, {
        backdrop: 'static',
        keyboard: true
      })
    }

    // Find elements within the modal
    const currentStatusText = modalElement.querySelector('[data-decision-modal-target="currentStatusText"]')
    const form = modalElement.querySelector('[data-decision-modal-target="form"]')
    const statusInput = modalElement.querySelector('[data-decision-modal-target="statusInput"]')
    const submitButton = modalElement.querySelector('[data-decision-modal-target="submitButton"]')

    // Update current status display
    if (currentStatusText) {
      currentStatusText.textContent = this.currentStatusValue.toUpperCase().replace('_', ' ')
    }

    // Update form action URL with the token
    if (form) {
      const currentAction = form.action
      const newAction = currentAction.replace(/\/dossier\/[^\/]+\//, `/dossier/${this.tokenValue}/`)
      form.action = newAction
    }

    // Reset form
    this.resetModalForm(modalElement)

    // Set up event handlers for this modal instance
    const handleStatusSelection = (e) => {
      if (e.target.closest('.status-card')) {
        const statusCard = e.target.closest('.status-card')
        const selectedStatus = statusCard.dataset.status

        // Update hidden field
        if (statusInput) {
          statusInput.value = selectedStatus
        }

        // Update visual state
        modalElement.querySelectorAll('.status-card').forEach(card => {
          card.classList.remove('status-card--selected')
        })
        statusCard.classList.add('status-card--selected')

        // Enable submit button if different status
        if (submitButton) {
          const isDifferent = selectedStatus !== this.currentStatusValue
          submitButton.disabled = !isDifferent
        }
      }
    }

    const handleSubmit = (e) => {
      const spinner = modalElement.querySelector('[data-decision-modal-target="spinner"]')
      const submitText = modalElement.querySelector('[data-decision-modal-target="submitText"]')

      if (spinner) spinner.classList.remove('d-none')
      if (submitButton) submitButton.disabled = true
      if (submitText) {
        if (!submitText.dataset.originalText) {
          submitText.dataset.originalText = submitText.textContent
        }
        submitText.textContent = 'Changing...'
      }
    }

    const handleModalHidden = () => {
      this.resetModalForm(modalElement)
      modalElement.removeEventListener('click', handleStatusSelection)
      if (form) form.removeEventListener('submit', handleSubmit)
      modalElement.removeEventListener('hidden.bs.modal', handleModalHidden)
    }

    // Add event listeners
    modalElement.addEventListener('click', handleStatusSelection)
    if (form) form.addEventListener('submit', handleSubmit)
    modalElement.addEventListener('hidden.bs.modal', handleModalHidden, { once: true })

    // Show the modal
    modalInstance.show()
  }

  resetModalForm(modalElement) {
    // Clear status selection
    const statusInput = modalElement.querySelector('[data-decision-modal-target="statusInput"]')
    if (statusInput) statusInput.value = ''

    // Remove selected state from all cards
    modalElement.querySelectorAll('.status-card').forEach(card => {
      card.classList.remove('status-card--selected')
    })

    // Disable submit button
    const submitButton = modalElement.querySelector('[data-decision-modal-target="submitButton"]')
    if (submitButton) submitButton.disabled = true

    // Reset submit button text
    const submitText = modalElement.querySelector('[data-decision-modal-target="submitText"]')
    if (submitText && submitText.dataset.originalText) {
      submitText.textContent = submitText.dataset.originalText
    }

    // Hide spinner
    const spinner = modalElement.querySelector('[data-decision-modal-target="spinner"]')
    if (spinner) spinner.classList.add('d-none')

    // Clear reason textarea
    const reasonField = modalElement.querySelector('textarea[name="reason"]')
    if (reasonField) reasonField.value = ''
  }

  injectStyles() {
    // Check if styles already injected
    if (document.getElementById('decision-modal-styles')) {
      return
    }

    const style = document.createElement('style')
    style.id = 'decision-modal-styles'
    style.textContent = `
      /* Status card styling */
      .status-card {
        cursor: pointer;
        transition: all 200ms ease-out !important;
        border: 2px solid #dee2e6 !important;
        height: 100% !important;
        display: flex !important;
        flex-direction: column !important;
        align-items: center !important;
        background-color: #ffffff;
      }

      .status-card:hover {
        border-color: var(--bs-primary) !important;
        transform: translateY(-2px);
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
      }

      .status-card--selected {
        border-color: var(--bs-primary) !important;
        background-color: rgba(var(--bs-primary-rgb), 0.05) !important;
        box-shadow: 0 0 0 3px rgba(var(--bs-primary-rgb), 0.1) !important;
      }

      .status-card .icon {
        font-size: 2rem;
      }

      .status-content {
        flex-grow: 1;
        display: flex;
        flex-direction: column;
        justify-content: center;
      }

      /* Hide radio buttons */
      .status-option input[type="radio"] {
        position: absolute;
        opacity: 0;
        pointer-events: none;
      }

      /* Ensure consistent heights */
      .status-selection-options .row {
        display: flex;
        flex-wrap: wrap;
      }

      .status-selection-options .col-md-4 {
        display: flex;
        margin-bottom: 0 !important;
      }

      /* Dark mode support */
      :root[data-bs-theme="dark"] .status-card {
        background-color: var(--bolt-elements-bg-depth-2, #1a1a1a);
        border-color: var(--bolt-elements-borderColor, #333) !important;
      }

      :root[data-bs-theme="dark"] .status-card:hover {
        border-color: var(--bs-primary) !important;
        background-color: rgba(var(--bs-primary-rgb), 0.1) !important;
      }

      :root[data-bs-theme="dark"] .status-card--selected {
        background-color: rgba(var(--bs-primary-rgb), 0.15) !important;
      }
    `
    document.head.appendChild(style)
  }
}
